import { HttpErrorResponse } from "@angular/common/http";
import { Component, Inject, OnInit } from "@angular/core";
import { FormControl, FormGroup } from "@angular/forms";
import {
	MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA,
	MatLegacyDialog as MatDialog,
	MatLegacyDialogRef as MatDialogRef,
} from "@angular/material/legacy-dialog";
import { ElRefDirective } from "@app/_directives/el-ref/el-ref.directive";
import { pushError } from "@app/_helpers/globalErrorHandler";
import parseSubscriptionAsStatus from "@app/_helpers/parseSubscriptionAsStatus";
import { getTimesFutureLockDate, getTimesLockedDate } from "@app/_helpers/timeLock";
import { DEFAULT_PERMISSION_GROUPS, fromRxValue, hasPermission } from "@app/_helpers/utils";
import {
	bulkEditLoadingState,
	shouldShowBulkEditProgress,
} from "@app/components/bulk-edit-progress-overlay/bulk-edit-loading.util";
import type { BulkEditLoadingPhase } from "@app/components/bulk-edit-progress-overlay/bulk-edit-progress-overlay.component";
import { EditUndoService } from "@app/services/edit-undo/edit-undo.service";
import type { TimeBulkEditDialogCloseResult } from "@app/services/edit-undo/edit-undo.types";
import { MixedTimesSyncService } from "@app/services/mixed-times-sync.service";
import {
	ClientProjectPickerStepperData,
	ClientProjectPickerStepperDialogComponent,
} from "@app/shared/dialogs/client-project-picker-stepper-dialog/client-project-picker-stepper-dialog.component";
import { TagPickerDialogComponent } from "@app/shared/dialogs/tag-picker-dialog/tag-picker-dialog.component";
import { RecordToolbarService } from "@app/shared/record-toolbar/record-toolbar.service";
import { TranslateService } from "@ngx-translate/core";
import {
	Logger,
	MyTimesService,
	Project,
	ProjectsQuery,
	TagType,
	Task,
	Time,
	UserSettingsQuery,
} from "@timeghost/web-api";
import {
	addSeconds,
	differenceInSeconds,
	format as formatString,
	isValid,
	parse as parseFromString,
	startOfDay,
} from "date-fns/esm";
import { flow } from "lodash-es";
import { MediaObserver } from "ngx-flexible-layout/core";
import { map } from "rxjs/operators";
import { parseDurationAsFormat, parseDurationSeconds } from "../duration-input-control/duration-input-utils";
import { TaskPickerDialogComponent } from "../task-picker-dialog/task-picker-dialog.component";
import {
	UserSinglePickerDialogComponent,
	UserSinglePickerDialogData,
} from "../user-single-picker-dialog/user-single-picker-dialog.component";

const log = new Logger("TimeBulkEditDialogComponent");

interface BulkEditProgress {
	total: number;
	updated: number;
	failed: number;
}

interface TimeBulkEditFormValue {
	billable: boolean;
	date: Date | null;
	duration: string | null;
	end: string | null;
	name: string | null;
	preserveBillable: boolean;
	preserveTime: boolean;
	project: Project | null;
	replaceTags: boolean;
	start: string | null;
	tags: Time["tags"] | null;
	task: Task | null;
	user: Time["user"] | null;
}
@Component({
	selector: "tg-time-bulk-edit-dialog",
	templateUrl: "./time-bulk-edit-dialog.component.html",
	styleUrls: ["./time-bulk-edit-dialog.component.scss"],
})
export class TimeBulkEditDialogComponent implements OnInit {
	constructor(
		private ref: MatDialogRef<TimeBulkEditDialogComponent>,
		@Inject(MAT_DIALOG_DATA)
		private data: any,
		private userSettingsQuery: UserSettingsQuery,
		private media: MediaObserver,
		private projectsQuery: ProjectsQuery,
		private translate: TranslateService,
		private myTimesService: MyTimesService,
		private dialog: MatDialog,
		private recordService: RecordToolbarService,
		private editUndoService: EditUndoService,
		private mixedTimesSyncService: MixedTimesSyncService
	) {}
	group = (() => {
		const now = new Date();
		return new FormGroup({
			name: new FormControl(this.data.name),
			project: new FormControl(this.data.project),
			task: new FormControl(this.data.task),
			start: new FormControl(this.data.start),
			end: new FormControl(this.data.end),
			duration: new FormControl(
				this.data.timeDiff >= 0
					? parseDurationAsFormat(this.data.timeDiff / 60)
					: (([start, end]) => {
							if (!isValid(start) || !isValid(end)) return "00:00";
							return flow(
								(d) => addSeconds(d, (end.getTime() - start.getTime()) / 1000),
								(d) => formatString(d, "HH:mm")
							)(startOfDay(new Date()));
						})([new Date(this.data.start), new Date(this.data.end)])
			),
			date: new FormControl(this.data.date),
			billable: new FormControl(!!this.data.billable),
			tags: new FormControl(this.data.tags),
			user: new FormControl(this.data.user),
			preserveTime: new FormControl(true),
			preserveBillable: new FormControl(true),
			replaceTags: new FormControl(false),
		}).setMustChange();
	})();
	showPreserveToggle = !!this.data.isMixed;
	canEdit = !!this.data.canEdit;
	get entityCount() {
		return this.data.entities.length;
	}

	bulkProgress: BulkEditProgress = { total: 0, updated: 0, failed: 0 };
	loadingPhase: BulkEditLoadingPhase = "validating";
	loadingPercent = 0;
	private _isLoading: boolean;
	public get isLoading(): boolean {
		return this._isLoading;
	}
	public set isLoading(v: boolean) {
		this._isLoading = v;
		if (this.ref && this.ref.disableClose !== v) this.ref.disableClose = v;
	}
	get showProgressOverlay(): boolean {
		return !!this.isLoading && shouldShowBulkEditProgress(this.bulkProgress.total);
	}
	private setLoadingProgress(processed?: number) {
		const total = this.bulkProgress.total;
		const current = processed ?? this.bulkProgress.updated + this.bulkProgress.failed;
		const state = bulkEditLoadingState(current, total);
		this.loadingPhase = state.phase;
		this.loadingPercent = state.percent;
	}
	calcDuration() {
		const { start, end } = this.group.value;
		const now = new Date();
		const duration =
			differenceInSeconds(parseFromString(end, "HH:mm", now), parseFromString(start, "HH:mm", now)).clampThis(0) /
			60;
		this.group.patchValue({
			duration,
		});
	}
	parseDuration(start: Date, end: Date) {
		const duration = differenceInSeconds(start, end).clampThis(0);

		return duration;
	}
	get mode() {
		return this.userSettingsQuery.getValue()?.workspace.settings?.timesMode ?? "range";
	}
	private _stateVisibleMode = this.mode === "range" ? true : this.data.inputMode === "duration";
	get stateVisibleMode() {
		return this._stateVisibleMode;
	}
	set stateVisibleMode(v: boolean) {
		if (this.userSettingsQuery.getValue().workspace?.settings?.timesMode === "range_optional") {
			this._stateVisibleMode = v;
			if (!v) {
				this.calcDuration();
			}
		}
	}
	ngOnInit(): void {
		this.ref._containerInstance._config.closeOnNavigation = true;
		this.ref.updateSize("480px");
		this.ref.addPanelClass(["mat-dialog-relative"]);
		log.debug("bulk", this.group.value);
	}
	workspace$isAdmin = fromRxValue(
		this.userSettingsQuery.select((x) => hasPermission(DEFAULT_PERMISSION_GROUPS.Admin, x))
	);
	get isMobile() {
		return this.media.isActive(["xs", "sm"]);
	}
	async submit() {
		const times = this.data.entities as Time[];
		const originalsSnapshot = times.map((entry) => structuredClone(entry));
		const formValue = this.group.value as TimeBulkEditFormValue;
		const stateVisibleMode = this.stateVisibleMode;
		const inputMode = !formValue.preserveTime ? (stateVisibleMode ? "range" : "duration") : null;
		const nextDuration = formValue.duration ? parseDurationSeconds(formValue.duration) : null;
		const currentUser = this.userSettingsQuery
			.getValue()
			.workspace?.users?.find?.((x) => x.id === this.userSettingsQuery.getValue().id);
		const isValidDate = formValue.date && isValid(new Date(formValue.date));
		const isDateOrTimeDirty =
			this.group.controls.start.dirty || this.group.controls.end.dirty || this.group.controls.date.dirty;

		const newTimes = times.map((entry) => {
			const refDate = !isValidDate ? new Date(entry.start) : new Date(formValue.date);
			const time = this.resolveTimeWindow(entry, formValue, refDate, inputMode);
			const timeInputMode = inputMode ?? entry["inputMode"];
			const isRange = formValue.preserveTime ? !timeInputMode || timeInputMode === "range" : stateVisibleMode;
			const timeDuration = formValue.preserveTime
				? entry.timeDiff
				: isRange
					? this.parseDuration(new Date(time.start), new Date(time.end))
					: nextDuration || this.parseDuration(new Date(time.start), new Date(time.end));

			const nextProject = this.resolveFieldValue(formValue.project, entry.project);
			const nextTask = nextProject?.useAsDefault ? null : this.resolveFieldValue(formValue.task, entry.task);

			return {
				...entry,
				name: this.resolveOptionalName(formValue.name, entry.name),
				project: nextProject,
				task: nextTask,
				billable: formValue.preserveBillable ? entry.billable : !!formValue.billable,
				user: !currentUser?.admin || formValue.user == null ? entry.user : formValue.user,
				tags: this.resolveTags(entry.tags, formValue.tags, formValue.replaceTags),
				inputMode: timeInputMode,
				...(() => {
					return {
						...(isRange
							? {
									...(isDateOrTimeDirty ? { ...time, timeDiff: timeDuration } : {}),
								}
							: {
									...time,
									timeDiff: timeDuration,
								}),
					};
				})(),
			};
		});
		if (this.hasMissingRequiredTask(newTimes)) {
			this.isLoading = false;
			this.recordService.notifyError("errors.record.desc-required");
			return;
		}
		this.editUndoService.dismissAllPendingUndoToasts();

		this.bulkProgress = { total: newTimes.length, updated: 0, failed: 0 };
		this.isLoading = true;
		this.setLoadingProgress(0);

		const allResults: Time[] = [];
		const chunks = this.chunkArray(newTimes, 50);

		for (const chunk of chunks) {
			await this.processChunk(chunk, allResults);
		}

		this.finishBulkEdit(allResults, originalsSnapshot);
	}
	private chunkArray<T>(arr: T[], size: number): T[][] {
		const chunks: T[][] = [];
		for (let i = 0; i < arr.length; i += size) {
			chunks.push(arr.slice(i, i + size));
		}
		return chunks;
	}
	private async processChunk(chunk: Time[], allResults: Time[]) {
		try {
			const res = await this.myTimesService.update(...chunk).toPromise();
			if (!res?.length) throw new Error("Bulk chunk update returned no results");
			allResults.push(...res);
			this.bulkProgress.updated += res.length;
		} catch (err) {
			log.warn("Chunk update failed, retrying entries individually", err);
			await this.retryChunkEntriesIndividually(chunk, allResults);
			return;
		}
		this.setLoadingProgress();
	}

	private async retryChunkEntriesIndividually(chunk: Time[], allResults: Time[]) {
		for (const entry of chunk) {
			try {
				const res = await this.myTimesService.update(entry).toPromise();
				const updated = res?.[0];
				if (!updated) {
					this.bulkProgress.failed++;
					continue;
				}
				allResults.push(updated);
				this.bulkProgress.updated++;
			} catch (entryErr) {
				this.bulkProgress.failed++;
				log.error(entryErr);
				this.handleBulkUpdateError(entryErr);
			}
			this.setLoadingProgress();
		}
	}

	private finishBulkEdit(updatedResults: Time[], originals: Time[]) {
		const successCount = updatedResults.length;
		const failedCount = this.bulkProgress.failed;
		this.isLoading = false;
		this.loadingPercent = 0;

		if (!successCount) {
			pushError("errors.time.user");
			return;
		}

		const originalsById = new Map(originals.map((t) => [t.id, t]));
		for (const updated of updatedResults) {
			const original = originalsById.get(updated.id);
			if (original) {
				this.mixedTimesSyncService.handleProjectTimeDialogClose(updated, original);
			}
		}

		const closeResult: TimeBulkEditDialogCloseResult = {
			times: updatedResults,
			undoOffer: {
				originals,
				updatedResults,
				...(failedCount > 0 ? { partial: { updated: successCount, failed: failedCount } } : {}),
			},
		};
		this.ref.close(closeResult);
	}
	private handleBulkUpdateError(err: unknown) {
		if (err instanceof HttpErrorResponse && err.error === "only_assignedusers_can_book_on_task") {
			this.group.patchValue({ task: null });
		}
	}
	private resolveFieldValue<T>(nextValue: T | null | undefined, currentValue: T): T {
		return nextValue == null ? currentValue : nextValue;
	}
	private resolveOptionalName(nextName: string | null | undefined, currentName: string): string {
		// Keep legacy behavior: empty name means "do not change existing names".
		return !nextName ? currentName : nextName;
	}
	private resolveTags(
		currentTags: Time["tags"] = [],
		selectedTags: Time["tags"] | null,
		replaceTags: boolean
	): Time["tags"] {
		// Null means mixed/unset and should preserve all current values.
		if (selectedTags == null) return currentTags;
		return replaceTags ? selectedTags : currentTags.concat(selectedTags).uniqBy((tag) => tag.id);
	}
	private resolveTimeWindow(
		time: Time,
		value: TimeBulkEditFormValue,
		refDate: Date,
		inputMode: "range" | "duration" | null
	) {
		return {
			start: value.preserveTime
				? parseFromString(formatString(new Date(time.start), "HH:mm"), "HH:mm", refDate).toISOString()
				: inputMode === "range" && value.start
					? parseFromString(value.start, "HH:mm", refDate).toISOString()
					: startOfDay(new Date(time.start)).toISOString(),
			end: value.preserveTime
				? parseFromString(formatString(new Date(time.end), "HH:mm"), "HH:mm", refDate).toISOString()
				: inputMode === "range" && value.end
					? parseFromString(value.end, "HH:mm", refDate).toISOString()
					: startOfDay(new Date(time.end)).toISOString(),
		};
	}
	private hasMissingRequiredTask(times: Time[]): boolean {
		const requireTask = !!this.userSettingsQuery.getValue()?.workspace?.settings?.requireTask;
		if (!requireTask) return false;
		return times.some((time) => !time.project?.useAsDefault && !time.task?.id);
	}
	setTask(task?: Task) {
		if (!task) {
			this.group.patchValue({ task: null });
			return;
		}
		const project = this.resolveTaskProject(task) ?? this.group.value.project;
		if (!project) {
			this.group.patchValue({ task: null });
			return;
		}
		if (project.id !== task.project.id) {
			return;
		}
		this.group.patchValue({
			project,
			task,
		});
	}
	private resolveTaskProject(task: Task): Project | null {
		if (!task?.project?.id) return null;
		return (
			this.projectsQuery.getAll({
				filterBy: (entity) => entity.id === task.project.id,
			})[0] ?? null
		);
	}
	openTaskPicker() {
		if (this.group.value.project?.useAsDefault) return;
		return this.dialog
			.open(TaskPickerDialogComponent, {
				data: {
					project: this.group.value.project,
					task: this.group.value.task,
				},
			})
			.afterClosed()
			.subscribe((x) => {
				if (x !== undefined) this.setTask(x);
			});
	}
	openTagsPicker() {
		return this.dialog
			.open(TagPickerDialogComponent, {
				data: {
					data: {
						SelectedTags: this.group.value.tags ?? [],
						type: TagType.Time,
						allowTypeModify: false,
						canToggle: true,
					},
				},
			})
			.afterClosed()
			.subscribe((tags) => {
				if (tags !== undefined) {
					this.group.patchValue({
						tags,
					});
				}
			});
	}
	openUserPicker() {
		if (!this.workspace$isAdmin.value) return;
		return this.dialog
			.open(UserSinglePickerDialogComponent, {
				data: <UserSinglePickerDialogData>{
					selected: this.group.value?.user,
					filter: ({ selected, ...x }) => {
						const status = ((user) => parseSubscriptionAsStatus(user.workspace, user))(
							this.userSettingsQuery.getValue()
						);
						return ((x.has_license || status.isTrial) && !x.removed) || selected;
					},
				},
			})
			.afterClosed()
			.subscribe((user) => {
				if (user)
					this.group.patchValue({
						user,
					});
			});
	}
	openProjectPicker() {
		return this.dialog
			.open(ClientProjectPickerStepperDialogComponent, {
				data: <ClientProjectPickerStepperData>{
					skipProjectStepper: false,
					project: this.group.value.project,
					task: this.group.value.task,
				},
			})
			.afterClosed()
			.pipe(map((x) => x || []))
			.subscribe(([p, t]) => {
				if (p) this.setProject(p, p?.useAsDefault ? null : t);
			});
	}
	setProject(project?: Project, task?: Task) {
		if (
			project &&
			this.group.value.project?.id === project.id &&
			(task ? this.group.value.task?.id === task.id : true)
		)
			return;
		this.group.patchValue({
			project: project || this.projectsQuery.getAll({ filterBy: (x) => x.useAsDefault, limitTo: 1 })[0],
			task,
		});
	}
	togglePreserve(props: string[], preserve: boolean, value?: any) {
		return this.group.patchValue(
			props.map((key) => ({ [key]: preserve ? null : value })).reduce((l, r) => ({ ...l, ...r }), {})
		);
	}
	selectInput(ev: Event, timeInput: ElRefDirective) {
		const el: HTMLElement = timeInput.elementRef.nativeElement;
		if (!el) {
			return;
		}
		const input = el.querySelector("input");
		if (input) {
			ev.preventDefault();
			input.select();
		}
	}
	minDate() {
		if (!this.group.value.project) return null;
		const projectId = this.group.value.project.id;
		if (!projectId) return null;
		let project = this.projectsQuery.getAll().find((x) => x.id == projectId);
		return getTimesLockedDate(this.userSettingsQuery.getValue(), project);
	}
	maxDate() {
		if (!this.group.value.project) return null;
		const projectId = this.group.value.project.id;
		if (!projectId) return null;
		let project = this.projectsQuery.getAll().find((x) => x.id == projectId);
		return getTimesFutureLockDate(this.userSettingsQuery.getValue(), project);
	}
}
